/**
 * Content Manager - Notifications Service
 *
 * Notification system with in-app, email (AWS SES), and webhook support.
 */

import type {
  Notification,
  NotificationType,
  NotificationChannel,
  NotificationPreferences,
  ApiResponse,
} from '../types';
import { logger } from '../lib/logger';
import { teamsNotificationHelper } from './integrations';

// =============================================================================
// DEFAULT NOTIFICATION PREFERENCES
// =============================================================================

const DEFAULT_PREFERENCES: Omit<NotificationPreferences, 'userId'> = {
  channels: {
    inApp: true,
    email: true,
    teams: false,
  },
  types: {
    mention: ['in_app', 'email'],
    assignment: ['in_app', 'email'],
    approval_requested: ['in_app', 'email'],
    approval_decision: ['in_app', 'email'],
    status_change: ['in_app'],
    due_date_reminder: ['in_app', 'email'],
    comment: ['in_app'],
    workflow_complete: ['in_app', 'email'],
  },
  emailDigest: 'daily',
  digestTime: '09:00',
  quietHoursEnabled: false,
  quietHoursStart: '22:00',
  quietHoursEnd: '08:00',
};

// =============================================================================
// SERVICE
// =============================================================================

class NotificationsService {
  private notifications: Notification[] = [];
  private preferences: Map<string, NotificationPreferences> = new Map();
  private subscribers: Set<(notifications: Notification[]) => void> = new Set();

  /**
   * Get all notifications for a user
   */
  async getForUser(userId: string, options?: {
    unreadOnly?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<Notification[]>> {
    try {
      let result = this.notifications.filter((n) => n.userId === userId);

      if (options?.unreadOnly) {
        result = result.filter((n) => !n.isRead);
      }

      // Sort by most recent first
      result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      if (options?.offset) {
        result = result.slice(options.offset);
      }
      if (options?.limit) {
        result = result.slice(0, options.limit);
      }

      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get unread count for a user
   */
  async getUnreadCount(userId: string): Promise<ApiResponse<number>> {
    try {
      const count = this.notifications.filter(
        (n) => n.userId === userId && !n.isRead
      ).length;
      return { data: count, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Create a notification
   */
  async create(
    notification: Omit<Notification, 'id' | 'isRead' | 'sentAt' | 'createdAt'>
  ): Promise<ApiResponse<Notification>> {
    try {
      // Get user preferences
      const prefs = this.preferences.get(notification.userId) || {
        ...DEFAULT_PREFERENCES,
        userId: notification.userId,
      };

      // Determine which channels to send to
      const channelsToUse = prefs.types[notification.type] || ['in_app'];

      // Check quiet hours
      if (prefs.quietHoursEnabled) {
        const now = new Date();
        const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        if (currentTime >= prefs.quietHoursStart || currentTime <= prefs.quietHoursEnd) {
          // Only send in-app during quiet hours, queue email for later
          channelsToUse.splice(channelsToUse.indexOf('email'), 1);
        }
      }

      const now = new Date().toISOString();
      const sentAt: Record<NotificationChannel, string | null> = {
        in_app: channelsToUse.includes('in_app') ? now : null,
        email: null,
        teams: null,
      };

      const newNotification: Notification = {
        ...notification,
        id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        isRead: false,
        channels: channelsToUse,
        sentAt,
        createdAt: now,
      };

      this.notifications.push(newNotification);

      // Send to external channels
      await this.sendToChannels(newNotification, channelsToUse);

      // Notify subscribers
      this.notifySubscribers();

      logger.info('content', 'Created notification', {
        type: notification.type,
        userId: notification.userId,
        channels: channelsToUse,
      });

      return { data: newNotification, error: null };
    } catch (error) {
      logger.error('content', 'Failed to create notification', error);
      return { data: null, error: error as Error };
    }
  }

  /**
   * Mark notification as read
   */
  async markAsRead(notificationId: string): Promise<ApiResponse<boolean>> {
    try {
      const notification = this.notifications.find((n) => n.id === notificationId);
      if (!notification) {
        return { data: null, error: new Error('Notification not found') };
      }

      notification.isRead = true;
      this.notifySubscribers();

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<ApiResponse<number>> {
    try {
      let count = 0;
      this.notifications.forEach((n) => {
        if (n.userId === userId && !n.isRead) {
          n.isRead = true;
          count++;
        }
      });

      this.notifySubscribers();
      return { data: count, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Delete a notification
   */
  async delete(notificationId: string): Promise<ApiResponse<boolean>> {
    try {
      const index = this.notifications.findIndex((n) => n.id === notificationId);
      if (index === -1) {
        return { data: null, error: new Error('Notification not found') };
      }

      this.notifications.splice(index, 1);
      this.notifySubscribers();

      return { data: true, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Get user preferences
   */
  async getPreferences(userId: string): Promise<ApiResponse<NotificationPreferences>> {
    try {
      const prefs = this.preferences.get(userId) || {
        ...DEFAULT_PREFERENCES,
        userId,
      };
      return { data: prefs, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Update user preferences
   */
  async updatePreferences(
    userId: string,
    updates: Partial<NotificationPreferences>
  ): Promise<ApiResponse<NotificationPreferences>> {
    try {
      const current = this.preferences.get(userId) || {
        ...DEFAULT_PREFERENCES,
        userId,
      };

      const updated = {
        ...current,
        ...updates,
        userId,
      };

      this.preferences.set(userId, updated);
      logger.info('content', 'Updated notification preferences', { userId });

      return { data: updated, error: null };
    } catch (error) {
      return { data: null, error: error as Error };
    }
  }

  /**
   * Subscribe to notification updates
   */
  subscribe(callback: (notifications: Notification[]) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Send notification to external channels (email, Teams)
   */
  private async sendToChannels(
    notification: Notification,
    channels: NotificationChannel[]
  ): Promise<void> {
    for (const channel of channels) {
      try {
        switch (channel) {
          case 'email':
            await this.sendEmail(notification);
            break;
          case 'teams':
            await this.sendTeams(notification);
            break;
          // in_app is handled by the notification store
        }
      } catch (error) {
        logger.error('content', `Failed to send to ${channel}`, error);
      }
    }
  }

  /**
   * Send email notification via AWS SES
   */
  private async sendEmail(notification: Notification): Promise<void> {
    // In production, this would use AWS SES SDK
    // const ses = new SESClient({ region: 'us-east-1' });
    // await ses.send(new SendEmailCommand({...}));

    logger.info('content', 'Email notification queued', {
      type: notification.type,
      userId: notification.userId,
    });

    // Mark as sent
    notification.sentAt.email = new Date().toISOString();
  }

  /**
   * Send Teams notification
   */
  private async sendTeams(notification: Notification): Promise<void> {
    if (!teamsNotificationHelper.isConfigured()) {
      logger.info('content', 'Teams not configured, skipping notification', {
        type: notification.type,
        userId: notification.userId,
      });
      return;
    }

    try {
      // Map notification types to Teams messages
      const colorMap: Record<NotificationType, string> = {
        mention: '#3b82f6',
        assignment: '#8b5cf6',
        approval_requested: '#f59e0b',
        approval_decision: '#22c55e',
        status_change: '#0078D4',
        due_date_reminder: '#ef4444',
        comment: '#6b7280',
        workflow_complete: '#10b981',
      };

      await teamsNotificationHelper.sendNotification(
        notification.title,
        notification.message,
        colorMap[notification.type] || '#0078D4',
        [
          { title: 'Type', value: notification.type.replace('_', ' ') },
          { title: 'Content', value: notification.entityTitle || 'N/A' },
        ]
      );

      logger.info('content', 'Teams notification sent', {
        type: notification.type,
        userId: notification.userId,
      });

      notification.sentAt.teams = new Date().toISOString();
    } catch (error) {
      logger.error('content', 'Failed to send Teams notification', error);
    }
  }

  /**
   * Notify all subscribers
   */
  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => callback(this.notifications));
  }

  /**
   * Create mention notification
   */
  async notifyMention(
    mentionedUserId: string,
    mentionerName: string,
    entityType: 'seed' | 'output' | 'note',
    entityId: string,
    entityTitle: string
  ): Promise<void> {
    await this.create({
      userId: mentionedUserId,
      type: 'mention',
      title: 'You were mentioned',
      message: `${mentionerName} mentioned you in ${entityTitle}`,
      entityType: entityType as any,
      entityId,
      entityTitle,
      channels: ['in_app', 'email'],
    });
  }

  /**
   * Create assignment notification
   */
  async notifyAssignment(
    assigneeId: string,
    assignerName: string,
    entityType: 'seed' | 'output',
    entityId: string,
    entityTitle: string
  ): Promise<void> {
    await this.create({
      userId: assigneeId,
      type: 'assignment',
      title: 'New Assignment',
      message: `${assignerName} assigned you to ${entityTitle}`,
      entityType,
      entityId,
      entityTitle,
      channels: ['in_app', 'email'],
    });
  }

  /**
   * Create approval request notification
   */
  async notifyApprovalRequest(
    approverIds: string[],
    requesterName: string,
    entityType: 'seed' | 'output',
    entityId: string,
    entityTitle: string,
    workflowId: string
  ): Promise<void> {
    for (const userId of approverIds) {
      await this.create({
        userId,
        type: 'approval_requested',
        title: 'Approval Required',
        message: `${requesterName} requested your approval for ${entityTitle}`,
        entityType: 'workflow',
        entityId: workflowId,
        entityTitle,
        channels: ['in_app', 'email'],
      });
    }
  }

  /**
   * Create due date reminder notification
   */
  async notifyDueDateReminder(
    userId: string,
    entityType: 'seed' | 'output',
    entityId: string,
    entityTitle: string,
    dueDate: string
  ): Promise<void> {
    await this.create({
      userId,
      type: 'due_date_reminder',
      title: 'Due Date Approaching',
      message: `${entityTitle} is due on ${new Date(dueDate).toLocaleDateString('en-GB')}`,
      entityType,
      entityId,
      entityTitle,
      channels: ['in_app', 'email'],
    });
  }

  /**
   * Get notification statistics
   */
  getStats(userId?: string): {
    total: number;
    unread: number;
    byType: Record<NotificationType, number>;
  } {
    let notifications = this.notifications;
    if (userId) {
      notifications = notifications.filter((n) => n.userId === userId);
    }

    const byType: Record<NotificationType, number> = {
      mention: 0,
      assignment: 0,
      approval_requested: 0,
      approval_decision: 0,
      status_change: 0,
      due_date_reminder: 0,
      comment: 0,
      workflow_complete: 0,
    };

    notifications.forEach((n) => {
      byType[n.type]++;
    });

    return {
      total: notifications.length,
      unread: notifications.filter((n) => !n.isRead).length,
      byType,
    };
  }
}

export const notificationsService = new NotificationsService();
export default notificationsService;
